import { licenseChange } from 'constants/licenses';
import { SKILL_DIVE_TOGGLE_TOOLTIPS, SkillDiveToggleReason } from 'constants/skill_dive';

export enum LicenseErrorCode {
  TRAINING_LICENSES_EXHAUSTED = 'training_licenses_exhausted',
  SKILL_DIVE_LICENSES_EXHAUSTED = 'skill_dive_licenses_exhausted',
  SKILL_DIVE_REQUIRES_TRAINING_LICENSE = 'skill_dive_requires_training_license',
  SKILL_DIVE_NOT_ON_PLAN = 'skill_dive_not_on_plan',
  ACCOUNT_UPDATING = 'account_updating',
  CAPACITY_BELOW_COMMITTED = 'capacity_below_committed',
}

// That tooltip has no closing period, and callers add a sentence after the message.
const NO_TRAINING_MESSAGE = `${SKILL_DIVE_TOGGLE_TOOLTIPS[SkillDiveToggleReason.NO_TRAINING]}.`;

// The Skill Dive codes describe the same conditions as the toggle tooltips, so they share that copy.
export const LICENSE_ERROR_MESSAGES: Record<LicenseErrorCode, string> = {
  [LicenseErrorCode.TRAINING_LICENSES_EXHAUSTED]:
    "Your account doesn't have any training licenses left. Free up a license or contact Sales to add more.",
  [LicenseErrorCode.SKILL_DIVE_LICENSES_EXHAUSTED]:
    SKILL_DIVE_TOGGLE_TOOLTIPS[SkillDiveToggleReason.EXHAUSTED],
  [LicenseErrorCode.SKILL_DIVE_REQUIRES_TRAINING_LICENSE]: NO_TRAINING_MESSAGE,
  [LicenseErrorCode.SKILL_DIVE_NOT_ON_PLAN]:
    SKILL_DIVE_TOGGLE_TOOLTIPS[SkillDiveToggleReason.NOT_ON_PLAN],
  [LicenseErrorCode.ACCOUNT_UPDATING]:
    'Your account is being updated right now. Please try again in a moment.',
  [LicenseErrorCode.CAPACITY_BELOW_COMMITTED]:
    'Your account has more licenses assigned or reserved than this change allows.',
};

const isLicenseErrorCode = (code: unknown): code is LicenseErrorCode =>
  typeof code === 'string' && (Object.values(LicenseErrorCode) as string[]).includes(code);

export interface LicenseErrorInfo {
  code: LicenseErrorCode;
  message: string;
}

interface ErrorWithCode {
  response?: { data?: { code?: unknown } };
}

// `code` sits at the top level of both error body shapes; an unknown code returns null.
export const getLicenseErrorInfo = (error: unknown): LicenseErrorInfo | null => {
  const code = (error as ErrorWithCode)?.response?.data?.code;
  if (!isLicenseErrorCode(code)) return null;
  return {
    code,
    message: LICENSE_ERROR_MESSAGES[code],
  };
};

// Added after a code's message: each batch is all-or-nothing.
export const LICENSE_REJECTION_OUTCOMES = {
  NO_INVITES_SENT: 'No invites were sent.',
  NO_EMAIL_INVITATIONS_SENT: 'No email invitations were sent.',
  NO_INVITATIONS_CHANGED: 'No invitations were changed.',
  LINK_NOT_CREATED: 'The invite link wasn’t created.',
  LINK_NOT_CHANGED: 'The invite link wasn’t changed.',
} as const;

export const licensesNotChanged = (assign: boolean): string =>
  `No licenses were ${licenseChange(assign)}.`;

export const licenseRejectionMessage = ({ message }: LicenseErrorInfo, outcome: string): string =>
  `${message} ${outcome}`;
